import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart3, Loader2, FileSpreadsheet, Printer, Users, Wallet, TrendingUp, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { money } from "../../lib/jobCalc";

const TABS = [
  { key: "jobs-by-customer", label: "Jobs by customer", icon: Users },
  { key: "revenue", label: "Revenue", icon: TrendingUp },
  { key: "outstanding", label: "Outstanding by customer", icon: Wallet },
  { key: "insurer", label: "Insurer report", icon: ShieldCheck },
];

const todayIso = () => new Date().toISOString().slice(0, 10);
const monthsAgo = (n) => {
  const d = new Date(); d.setMonth(d.getMonth() - n);
  return d.toISOString().slice(0, 10);
};

export const AdminReports = () => {
  const [tab, setTab] = useState("jobs-by-customer");
  const [dateFrom, setDateFrom] = useState(monthsAgo(6));
  const [dateTo, setDateTo] = useState(todayIso());
  const [groupBy, setGroupBy] = useState("month");
  const [paymentType, setPaymentType] = useState("");
  const [insurer, setInsurer] = useState("");
  const [fitter, setFitter] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = useMemo(() => {
    const p = { date_from: dateFrom, date_to: dateTo };
    if (tab === "revenue") {
      if (groupBy) p.group_by = groupBy;
      if (paymentType) p.payment_type = paymentType;
      if (insurer) p.insurer = insurer;
      if (fitter) p.fitter = fitter;
    }
    return p;
  }, [tab, dateFrom, dateTo, groupBy, paymentType, insurer, fitter]);

  const load = async () => {
    setLoading(true); setData(null);
    try {
      if (tab === "jobs-by-customer") setData(await adminApi.reportJobsByCustomer(params));
      else if (tab === "revenue") setData(await adminApi.reportRevenue(params));
      else if (tab === "outstanding") setData(await adminApi.reportOutstanding());
      else if (tab === "insurer") setData(await adminApi.reportInsurer(params));
    } catch { toast.error("Could not load report"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const printDoc = () => {
    document.body.classList.add("printing-doc");
    setTimeout(() => { window.print(); setTimeout(() => document.body.classList.remove("printing-doc"), 500); }, 50);
  };

  const excelUrl = adminApi.reportExcelUrl(tab, tab === "outstanding" ? {} : params);
  const pdfUrl = adminApi.reportPdfUrl(tab, tab === "outstanding" ? {} : params);

  return (
    <div data-testid="admin-reports-page">
      <div className="no-print">
        <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Reports</h1>
        <p className="text-[#3f4b52] mt-1">Financial + operational reports. Export any view to Excel or PDF.</p>

        <div className="mt-6 flex flex-wrap gap-2">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => { setData(null); setTab(key); }}
              data-testid={`report-tab-${key}`}
              className={`px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 border ${tab === key ? "bg-[#111619] text-white border-[#111619]" : "bg-white text-[#3f4b52] border-[#cfd7db] hover:border-[#f28a16]"}`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-4 bg-white border border-[#cfd7db] rounded-md p-4 flex flex-wrap items-end gap-3">
          {tab !== "outstanding" && (
            <>
              <div>
                <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest mb-1">From</div>
                <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-10 px-3 bg-white border border-[#cfd7db] rounded-md text-sm" />
              </div>
              <div>
                <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest mb-1">To</div>
                <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-10 px-3 bg-white border border-[#cfd7db] rounded-md text-sm" />
              </div>
            </>
          )}
          {tab === "revenue" && (
            <>
              <div>
                <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest mb-1">Group by</div>
                <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="h-10 px-3 bg-white border border-[#cfd7db] rounded-md text-sm">
                  <option value="month">Month</option>
                  <option value="fitter">Fitter</option>
                  <option value="insurer">Insurer</option>
                  <option value="payment_type">Payment type</option>
                </select>
              </div>
              <div>
                <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest mb-1">Payment type</div>
                <select value={paymentType} onChange={(e) => setPaymentType(e.target.value)} className="h-10 px-3 bg-white border border-[#cfd7db] rounded-md text-sm">
                  <option value="">Any</option>
                  <option value="cash">Cash</option>
                  <option value="insurance">Insurance</option>
                </select>
              </div>
              <div>
                <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest mb-1">Insurer contains</div>
                <input value={insurer} onChange={(e) => setInsurer(e.target.value)} className="h-10 px-3 bg-white border border-[#cfd7db] rounded-md text-sm" placeholder="Santam…" />
              </div>
              <div>
                <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest mb-1">Fitter</div>
                <input value={fitter} onChange={(e) => setFitter(e.target.value)} className="h-10 px-3 bg-white border border-[#cfd7db] rounded-md text-sm" placeholder="ALPHA" />
              </div>
            </>
          )}
          <button
            onClick={load}
            disabled={loading}
            data-testid="report-apply"
            className="ml-auto bg-[#20282c] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2"
          >
            {loading ? <Loader2 className="animate-spin" size={14} /> : <BarChart3 size={14} />} Apply
          </button>
          <button
            onClick={() => adminApi.openDoc(pdfUrl)}
            type="button"
            data-testid="report-pdf-download"
            className="bg-[#f28a16] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:bg-[#d97706]"
          >
            <FileSpreadsheet size={14} /> PDF
          </button>
          <button
            onClick={() => adminApi.openDoc(excelUrl, { action: "download" })}
            type="button"
            data-testid="report-excel"
            className="bg-[#10b981] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:bg-[#0f9770]"
          >
            <FileSpreadsheet size={14} /> Excel
          </button>
          <button onClick={printDoc} data-testid="report-print" className="border border-[#cfd7db] text-[#3f4b52] px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:bg-[#f5f6f6]">
            <Printer size={14} /> Print preview
          </button>
        </div>
      </div>

      {/* Report body */}
      <div className="mt-6 report-print">
        {loading ? (
          <div className="p-8 text-center text-[#3f4b52] inline-flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading…</div>
        ) : !data ? (
          <div className="p-8 text-center text-[#3f4b52] text-sm">Set filters and click Apply.</div>
        ) : (
          <ReportBody tab={tab} data={data} dateFrom={dateFrom} dateTo={dateTo} groupBy={groupBy} />
        )}
      </div>
    </div>
  );
};

// --------------------- Report bodies ---------------------

const ReportBody = ({ tab, data, dateFrom, dateTo, groupBy }) => {
  if (tab === "jobs-by-customer") return <JobsByCustomerReport data={data} dateFrom={dateFrom} dateTo={dateTo} />;
  if (tab === "revenue") return <RevenueReport data={data} dateFrom={dateFrom} dateTo={dateTo} groupBy={groupBy} />;
  if (tab === "outstanding") return <OutstandingReport data={data} />;
  if (tab === "insurer") return <InsurerReport data={data} dateFrom={dateFrom} dateTo={dateTo} />;
  return null;
};

const ReportShell = ({ title, subtitle, children }) => (
  <article className="bg-white border border-[#cfd7db] rounded-md p-8 max-w-6xl mx-auto" data-testid="report-doc">
    <header className="pb-4 mb-4 border-b border-[#111619]">
      <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Diesel &amp; Dust Group — Report</div>
      <h2 className="font-display font-black uppercase text-2xl text-[#111619] mt-1">{title}</h2>
      {subtitle && <div className="text-xs text-[#3f4b52] mt-1">{subtitle}</div>}
    </header>
    {children}
    <footer className="mt-8 pt-4 border-t border-[#cfd7db] text-xs text-[#9aa6ad] text-center">
      Generated {new Date().toLocaleString("en-ZA")} · E&amp;OE
    </footer>
  </article>
);

const JobsByCustomerReport = ({ data, dateFrom, dateTo }) => {
  if (!data?.groups) return null;
  return (
  <ReportShell title="Jobs by customer" subtitle={`Period: ${dateFrom || "all"} → ${dateTo || "today"} · ${data.totals.job_count} jobs`}>
    <table className="w-full text-sm">
      <thead className="bg-[#f5f6f6] uppercase tracking-widest text-xs text-[#3f4b52]">
        <tr>
          <th className="text-left px-3 py-2">Code</th>
          <th className="text-left px-3 py-2">Customer</th>
          <th className="text-right px-3 py-2">Jobs</th>
          <th className="text-right px-3 py-2">Billed</th>
          <th className="text-right px-3 py-2">Paid</th>
          <th className="text-right px-3 py-2">Outstanding</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#eef2f4]">
        {data.groups.map((g) => (
          <tr key={g.customer_code}>
            <td className="px-3 py-2 font-mono text-[#f28a16] font-bold">{g.customer_code}</td>
            <td className="px-3 py-2">
              {g.customer_id ? (
                <Link to={`/admin/customers/${g.customer_id}`} className="hover:text-[#f28a16]">{g.customer_name || "—"}</Link>
              ) : (
                <span className="text-[#3f4b52] italic">{g.customer_name || "Walk-in"}</span>
              )}
            </td>
            <td className="px-3 py-2 text-right">{g.jobs.length}</td>
            <td className="px-3 py-2 text-right">{money(g.billed)}</td>
            <td className="px-3 py-2 text-right text-[#10b981]">{money(g.paid)}</td>
            <td className={`px-3 py-2 text-right font-bold ${g.outstanding > 0 ? "text-[#dc2626]" : "text-[#10b981]"}`}>{money(g.outstanding)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot className="bg-[#111619] text-white">
        <tr>
          <td colSpan={2} className="px-3 py-2 font-display uppercase tracking-widest text-xs">Total</td>
          <td className="px-3 py-2 text-right font-bold">{data.totals.job_count}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.billed)}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.paid)}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.outstanding)}</td>
        </tr>
      </tfoot>
    </table>
  </ReportShell>
  );
};

const RevenueReport = ({ data, dateFrom, dateTo, groupBy }) => {
  if (!data?.rows) return null;
  return (
  <ReportShell title={`Revenue by ${groupBy}`} subtitle={`Period: ${dateFrom || "all"} → ${dateTo || "today"} · ${data.totals.job_count} jobs · Gross ${money(data.totals.gross)}`}>
    <table className="w-full text-sm">
      <thead className="bg-[#f5f6f6] uppercase tracking-widest text-xs text-[#3f4b52]">
        <tr>
          <th className="text-left px-3 py-2">{groupBy}</th>
          <th className="text-right px-3 py-2">Jobs</th>
          <th className="text-right px-3 py-2">Subtotal</th>
          <th className="text-right px-3 py-2">VAT</th>
          <th className="text-right px-3 py-2">Gross</th>
          <th className="text-right px-3 py-2">Paid</th>
          <th className="text-right px-3 py-2">Outstanding</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#eef2f4]">
        {data.rows.map((r) => (
          <tr key={r.key}>
            <td className="px-3 py-2 font-mono">{r.key}</td>
            <td className="px-3 py-2 text-right">{r.job_count}</td>
            <td className="px-3 py-2 text-right">{money(r.subtotal)}</td>
            <td className="px-3 py-2 text-right">{money(r.vat)}</td>
            <td className="px-3 py-2 text-right font-bold">{money(r.gross)}</td>
            <td className="px-3 py-2 text-right text-[#10b981]">{money(r.paid)}</td>
            <td className={`px-3 py-2 text-right font-bold ${r.outstanding > 0 ? "text-[#dc2626]" : "text-[#10b981]"}`}>{money(r.outstanding)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot className="bg-[#111619] text-white">
        <tr>
          <td className="px-3 py-2 font-display uppercase tracking-widest text-xs">Total</td>
          <td className="px-3 py-2 text-right font-bold">{data.totals.job_count}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.subtotal)}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.vat)}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.gross)}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.paid)}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.outstanding)}</td>
        </tr>
      </tfoot>
    </table>
  </ReportShell>
  );
};

const OutstandingReport = ({ data }) => {
  if (!data?.rows) return null;
  return (
  <ReportShell title="Outstanding by customer" subtitle={`As at ${data.as_at} · Total outstanding ${money(data.totals.total)}`}>
    <table className="w-full text-sm">
      <thead className="bg-[#f5f6f6] uppercase tracking-widest text-xs text-[#3f4b52]">
        <tr>
          <th className="text-left px-3 py-2">Code</th>
          <th className="text-left px-3 py-2">Customer</th>
          <th className="text-right px-3 py-2">0-30</th>
          <th className="text-right px-3 py-2">31-60</th>
          <th className="text-right px-3 py-2">61-90</th>
          <th className="text-right px-3 py-2">90+</th>
          <th className="text-right px-3 py-2">Total</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#eef2f4]">
        {data.rows.map((r, i) => (
          <tr key={r.customer_id || `wi-${i}`}>
            <td className="px-3 py-2 font-mono text-[#f28a16] font-bold">{r.customer_code}</td>
            <td className="px-3 py-2">{r.customer_name || <span className="italic text-[#3f4b52]">Walk-in</span>}</td>
            <td className="px-3 py-2 text-right">{money(r.b0_30)}</td>
            <td className="px-3 py-2 text-right">{money(r.b31_60)}</td>
            <td className="px-3 py-2 text-right">{money(r.b61_90)}</td>
            <td className={`px-3 py-2 text-right ${r.b90_plus > 0 ? "text-[#dc2626] font-bold" : ""}`}>{money(r.b90_plus)}</td>
            <td className="px-3 py-2 text-right font-bold">{money(r.total)}</td>
          </tr>
        ))}
        {data.rows.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#10b981]">No outstanding balances — all paid up!</td></tr>}
      </tbody>
      <tfoot className="bg-[#111619] text-white">
        <tr>
          <td colSpan={2} className="px-3 py-2 font-display uppercase tracking-widest text-xs">Total</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.b0_30)}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.b31_60)}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.b61_90)}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.b90_plus)}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.total)}</td>
        </tr>
      </tfoot>
    </table>
  </ReportShell>
  );
};

const InsurerReport = ({ data, dateFrom, dateTo }) => {
  if (!data?.rows) return null;
  return (
  <ReportShell title="Insurer report" subtitle={`Period: ${dateFrom || "all"} → ${dateTo || "today"} · ${data.totals.job_count} claims · Total value ${money(data.totals.claim_value)}`}>
    <table className="w-full text-sm">
      <thead className="bg-[#f5f6f6] uppercase tracking-widest text-xs text-[#3f4b52]">
        <tr>
          <th className="text-left px-3 py-2">Insurer</th>
          <th className="text-right px-3 py-2">Claims</th>
          <th className="text-right px-3 py-2">Claim value</th>
          <th className="text-right px-3 py-2">Excess billed</th>
          <th className="text-right px-3 py-2">Excess paid</th>
          <th className="text-right px-3 py-2">Excess outstanding</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[#eef2f4]">
        {data.rows.map((r) => (
          <tr key={r.insurer}>
            <td className="px-3 py-2">{r.insurer}</td>
            <td className="px-3 py-2 text-right">{r.job_count}</td>
            <td className="px-3 py-2 text-right font-bold">{money(r.claim_value)}</td>
            <td className="px-3 py-2 text-right">{money(r.excess_billed)}</td>
            <td className="px-3 py-2 text-right text-[#10b981]">{money(r.excess_paid)}</td>
            <td className={`px-3 py-2 text-right ${r.excess_outstanding > 0 ? "text-[#dc2626] font-bold" : ""}`}>{money(r.excess_outstanding)}</td>
          </tr>
        ))}
        {data.rows.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#3f4b52]">No insurance jobs in this period.</td></tr>}
      </tbody>
      <tfoot className="bg-[#111619] text-white">
        <tr>
          <td className="px-3 py-2 font-display uppercase tracking-widest text-xs">Total</td>
          <td className="px-3 py-2 text-right font-bold">{data.totals.job_count}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.claim_value)}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.excess_billed)}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.excess_paid)}</td>
          <td className="px-3 py-2 text-right font-bold">{money(data.totals.excess_outstanding)}</td>
        </tr>
      </tfoot>
    </table>
  </ReportShell>
  );
};

export default AdminReports;
